import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useShopContext } from "../app/ShopGuard";
import { CartLineItem } from "../features/cart/CartLineItem";
import { useCart } from "../features/cart/useCart";
import { useSetCartItem } from "../features/cart/useSetCartItem";
import { messages, minOrderHint } from "../lib/messages";
import { mainButton } from "../lib/telegram";

/**
 * CartPage — экран корзины (docs/07-miniapp-ux.md §2.4): список позиций,
 * предупреждения о недоступных товарах, проверка минимальной суммы.
 * Доставка на этом экране не известна (зависит от способа получения,
 * который выбирается на оформлении) — показываем как «рассчитывается
 * при оформлении», а «к оплате» здесь равно сумме товаров.
 */
export function CartPage() {
  const { shop } = useShopContext();
  const navigate = useNavigate();
  const cart = useCart();
  const setCartItem = useSetCartItem();

  const data = cart.data;
  const hasItems = Boolean(data && data.items.length > 0);
  const minOrderReached = data?.min_order_reached ?? true;

  useEffect(() => {
    if (!hasItems || !data) {
      mainButton.hide();
      return;
    }
    if (!minOrderReached) {
      mainButton.setText(minOrderHint(shop.min_order_total_text));
      mainButton.show();
      return;
    }
    mainButton.setText(messages.checkout);
    mainButton.show();
    return mainButton.onClick(() => navigate("../checkout"));
  }, [hasItems, data, minOrderReached, shop.min_order_total_text, navigate]);

  useEffect(() => {
    return () => mainButton.hide();
  }, []);

  if (cart.isPending) {
    return <CartSkeleton />;
  }

  if (cart.isError || !data) {
    return <p className="p-4 text-center text-hint">{messages.requestFailed}</p>;
  }

  if (data.items.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-text">{messages.emptyCart}</p>
        <button
          type="button"
          onClick={() => navigate("..")}
          className="rounded-card bg-button px-4 py-2 text-button-text"
        >
          {messages.backToCatalog}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg pb-24">
      <header className="p-4">
        <h1 className="text-lg font-semibold text-text">{messages.cartLabel}</h1>
      </header>

      {data.warnings.length > 0 ? (
        <div className="mx-4 mb-3 flex flex-col gap-1 rounded-card border border-accent bg-accent/10 p-3 text-sm text-text">
          {data.warnings.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </div>
      ) : null}

      <ul className="flex flex-col gap-3 px-4">
        {data.items.map((item) => (
          <CartLineItem key={item.product_id} item={item} onChangeQty={(qty) => setCartItem(item.product_id, qty)} />
        ))}
      </ul>

      <div className="mt-4 flex flex-col gap-1 px-4 text-sm">
        <div className="flex justify-between text-hint">
          <span>{messages.itemsTotal}</span>
          <span>{data.items_total_text}</span>
        </div>
        <div className="flex justify-between text-hint">
          <span>{messages.delivery}</span>
          <span>{messages.deliveryCalculatedAtCheckout}</span>
        </div>
        <div className="flex justify-between text-base font-semibold text-text">
          <span>{messages.total}</span>
          <span>{data.items_total_text}</span>
        </div>
      </div>

      {!minOrderReached ? (
        <p className="mx-4 mt-3 text-center text-sm text-hint">{minOrderHint(shop.min_order_total_text)}</p>
      ) : null}
    </div>
  );
}

function CartSkeleton() {
  return (
    <div className="flex min-h-screen flex-col gap-3 bg-bg p-4">
      <div className="h-20 animate-pulse rounded-card bg-surface" />
      <div className="h-20 animate-pulse rounded-card bg-surface" />
    </div>
  );
}
